import { createContext, useContext, useEffect, useReducer, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useDIContainer } from '@/container/RouteContainerProvider';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { AssistSurfaceController } from './AssistSurfaceController';
import { SurfaceAssistViewModel } from './SurfaceAssistViewModel';
import { usePageFilterUrlSync } from './usePageFilterUrlSync';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

interface AssistSurfaceValue {
    controller: AssistSurfaceController;
    vm: SurfaceAssistViewModel;
}

const AssistSurfaceContext = createContext<AssistSurfaceValue | null>(null);
const EMPTY_FILTERS: AnalyticsFilter[] = [];

/**
 * Provides the shared {@link AssistSurfaceController} (active filters) and
 * {@link SurfaceAssistViewModel} for the surface, so the title-bar search box,
 * the vis overlay, and the page body act on the same assist session. Both are
 * DI singletons, so the session persists across drill-in navigation; consumers
 * subscribe individually rather than re-rendering the page on every change.
 */
export function AssistSurfaceProvider({ surfaceId, children }: { surfaceId: string; children: ReactNode }): ReactElement {
    const container = useDIContainer();
    const valueRef = useRef<AssistSurfaceValue | null>(null);
    if (valueRef.current === null) {
        valueRef.current = { controller: container.resolve(AssistSurfaceController), vm: container.resolve(SurfaceAssistViewModel) };
    }
    const value = valueRef.current;
    useEffect(() => {
        value.controller.setSurface(surfaceId);
    }, [value, surfaceId]);
    useEffect(() => {
        const globalFilters = container.resolve(GlobalFilterController);
        return globalFilters.subscribe(() => value.controller.touch());
    }, [value, container]);
    usePageFilterUrlSync(value.controller, surfaceId);
    return <AssistSurfaceContext.Provider value={value}>{children}</AssistSurfaceContext.Provider>;
}

/** The surface controller, or null when rendered outside a provider. */
export function useAssistSurface(): AssistSurfaceController | null {
    return useContext(AssistSurfaceContext)?.controller ?? null;
}

/** The shared assist view model (subscribed), or null when rendered outside a provider. */
export function useAssistViewModel(): SurfaceAssistViewModel | null {
    const value = useContext(AssistSurfaceContext);
    const [, forceUpdate] = useReducer((count: number) => count + 1, 0);
    const vm = value?.vm ?? null;
    useEffect(() => {
        if (vm === null) return;
        return vm.subscribe(() => forceUpdate());
    }, [vm]);
    return vm;
}

/** The active ambient filters, subscribed; a stable empty array when unwrapped. */
export function useAssistFilters(): AnalyticsFilter[] {
    const controller = useContext(AssistSurfaceContext)?.controller ?? null;
    const [filters, setFilters] = useState<AnalyticsFilter[]>(controller?.activeFilters ?? EMPTY_FILTERS);
    useEffect(() => {
        if (controller === null) {
            setFilters(EMPTY_FILTERS);
            return;
        }
        setFilters(controller.activeFilters);
        return controller.subscribe(() => setFilters(controller.activeFilters));
    }, [controller]);
    return filters;
}
